import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';

import {
  DriverDaySignature,
  JobLoadSignature,
  JobSignature,
} from '~hooks/useSignatures/models';

const SIGNATURE_IMAGE_HEIGHT_IN_PX = 40;

interface SignaturesViewerProps {
  signatures: (DriverDaySignature | JobLoadSignature | JobSignature)[];
  sx?: SxProps<Theme>;
}

export default function SignaturesViewer({ signatures, sx }: SignaturesViewerProps) {
  return (
    <Box
      sx={[
        { display: 'flex', flexDirection: 'row', gap: 2, width: '100%' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {signatures.map((signature) => (
        <Box key={signature.id} sx={{ flex: 1 }}>
          <Box
            sx={(theme) => ({
              borderBottom: `2px solid ${theme.brandV2.colors.treadGray7}`,
            })}
          >
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                fontSize: '12px',
                fontWeight: 600,
                mb: 1,
              })}
            >
              {t(`ticket_drawer_v2.details.${snakeCase(signature.type)}`)}
            </Typography>

            {signature.imageUrl ? (
              <Box
                component="img"
                alt={t(`ticket_drawer_v2.details.${snakeCase(signature.type)}`)}
                src={signature.imageUrl}
                sx={{ maxHeight: `${SIGNATURE_IMAGE_HEIGHT_IN_PX}px` }}
              />
            ) : (
              <Box
                sx={(theme) => ({
                  bgcolor: theme.brandV2.colors.treadGray9,
                  height: `${SIGNATURE_IMAGE_HEIGHT_IN_PX}px`,
                  width: '100%',
                })}
              />
            )}
          </Box>
          {signature.lat && signature.lon && (
            <Typography sx={{ fontSize: '11px', mt: 1 }}>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${signature.lat},${signature.lon}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                {Number.parseFloat(signature.lat).toFixed(6)},{' '}
                {Number.parseFloat(signature.lon).toFixed(6)}
              </a>
            </Typography>
          )}

          {'name' in signature && signature.name && (
            <Typography sx={{ fontSize: '11px', mt: 0.5 }}>
              {t('ticket_drawer_v2.details.signature_name')}: {signature.name}
            </Typography>
          )}

          {'phone' in signature && signature.phone && (
            <Typography sx={{ fontSize: '11px', mt: 0.5 }}>
              {t('ticket_drawer_v2.details.signature_phone')}: {signature.phone}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
